import { CheckStatus, GetChecksRequestDTO } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { getActiveWorkspaceId } from '@/stores/user.store';

export const checkKeys = {
  all: ['checks'] as const,
  detail: (checkId: string | null, isAWA: boolean) => [...checkKeys.all, 'detail', checkId, isAWA] as const,
  byAsset: (assetId: string) => [...checkKeys.all, 'by-asset', assetId] as const,
  awaByAsset: (assetId: string | undefined) => [...checkKeys.all, 'awa-by-asset', assetId] as const,
  upcomingSummary: () => [...checkKeys.all, 'upcoming-summary', getActiveWorkspaceId()] as const,
  upcomingDashboard: () => [...checkKeys.all, 'upcoming-dashboard', getActiveWorkspaceId()] as const,
  infinite: (filters?: GetChecksRequestDTO['filters'], groupBy?: GetChecksRequestDTO['groupBy'], orderBy?: GetChecksRequestDTO['orderBy']) =>
    [...checkKeys.all, 'infinite', filters, groupBy, orderBy] as const,
  actions: (checkId: string | undefined, isAWA: boolean) => [...checkKeys.all, 'actions', checkId, isAWA] as const,
};

export const getCheckActionsInfiniteQueryOptions = ({ checkId, isAWA = false }: { checkId: string | undefined; isAWA?: boolean }) => {
  return infiniteQueryOptions({
    queryKey: checkKeys.actions(checkId, isAWA),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (
        await (isAWA ? AWAApiV2Service : ApiService).GetActions({
          filters: { fields: { checkId: [checkId ?? ''] } },
          orderBy: { field: 'createdAt', direction: 'desc' },
          limit: 30,
          cursors: pageParam,
        })
      ).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    enabled: !!checkId,
  });
};

export const getCheckByIdQueryOptions = ({ checkId, isAWA = false }: { checkId: string | null; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: checkKeys.detail(checkId, isAWA),
    queryFn: async () => {
      if (!checkId) return null;
      return await (await (isAWA ? AWAApiV2Service : ApiService).GetCheckById({ checkId })).json();
    },
    enabled: !!checkId,
  });
};

export const getChecksByAssetQueryOptions = ({ assetId }: { assetId: string }) => {
  return queryOptions({
    queryKey: checkKeys.byAsset(assetId),
    queryFn: async () => (assetId ? await (await AWAApiV2Service.GetChecksByAsset({ assetId })).json() : null),
    enabled: !!assetId,
  });
};

export const getAWAChecksByAssetQueryOptions = ({ assetId }: { assetId: string | undefined }) => {
  return queryOptions({
    queryKey: checkKeys.awaByAsset(assetId),
    queryFn: async () => {
      if (!assetId) throw new Error('Asset ID is required');
      return (await AWAApiV2Service.GetChecksByAsset({ assetId, filter: { status: [CheckStatus.Open, CheckStatus.Closed] } })).json();
    },
    enabled: !!assetId,
  });
};

export const getUpcomingChecksSummaryQueryOptions = () => {
  return queryOptions({
    queryKey: checkKeys.upcomingSummary(),
    queryFn: async () => {
      const response = await ApiService.GetUpcomingChecksSummary();
      return response.json();
    },
  });
};

export const getChecksInfiniteQueryOptions = ({
  filters,
  groupBy,
  orderBy,
  enabled = true,
}: {
  filters?: GetChecksRequestDTO['filters'];
  groupBy?: GetChecksRequestDTO['groupBy'];
  orderBy?: GetChecksRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: checkKeys.infinite(filters, groupBy, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetChecks({ limit: 30, cursors: pageParam, filters, groupBy, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getUpcomingChecksDashboardNewQueryOptions = () => {
  return queryOptions({
    queryKey: checkKeys.upcomingDashboard(),
    queryFn: async () =>
      await (
        await ApiService.GetChecks({ limit: 3, orderBy: { field: 'dueProgress', direction: 'desc' }, filters: { fields: { status: [CheckStatus.Open] } } })
      ).json(),
  });
};
